// Copyright (c) 2026 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { fulfillmentsQueryKey } from '@/fulfillment/query-keys';
import { invalidateInventory } from '@/inventory/utilities/invalidate-inventory';
import {
  EditLineItemsRequest,
  editLineItemQuantities,
} from '@/shipment/api/edit-line-items';
import { shipmentDetailQueryKey } from '@/shipment/hooks/use-shipment';
import { shipmentsQueryKey } from '@/shipment/hooks/use-shipments';
import { useMutation, useQueryClient } from '@tanstack/react-query';

/**
 * React hook for editing line item quantities on a received shipment.
 *
 * Features:
 * - Mutations for editing bulk item quantities
 * - Automatic cache invalidation on success
 *
 * @param shipmentKey - The key of the shipment to edit
 * @returns Mutation object for editing line items
 */
export function useEditLineItems(shipmentKey: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: EditLineItemsRequest) =>
      editLineItemQuantities(shipmentKey, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: shipmentsQueryKey });
      void queryClient.invalidateQueries({
        queryKey: shipmentDetailQueryKey(shipmentKey),
      });
      void queryClient.invalidateQueries({ queryKey: fulfillmentsQueryKey });
      invalidateInventory(queryClient);
    },
  });
}
